import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { advanceReports } from '@/api/advanceReports';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { refs } from '@/api/refs';
import { amountToWordsUA } from '@/utils/numberToWordsUA';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MONTHS_UK = [
  'січня', 'лютого', 'березня', 'квітня', 'травня', 'червня',
  'липня', 'серпня', 'вересня', 'жовтня', 'листопада', 'грудня',
];

function formatDateUk(iso: string): string {
  const d = new Date(iso);
  return `« ${d.getDate()} » ${MONTHS_UK[d.getMonth()]!} ${d.getFullYear()} р.`;
}

function formatShortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export default function AdvanceReportPrintPage() {
  const { id } = useParams();
  const docQ = useQuery({
    queryKey: ['advance-report', id ?? ''],
    queryFn: () => advanceReports.get(id!),
    enabled: !!id,
  });
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const employeesQ = useQuery({
    queryKey: ['employees-all-print'],
    queryFn: () => refs.employees(),
  });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  const contractorName = useMemo(() => {
    const m = new Map<string, string>();
    contractorsQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [contractorsQ.data]);

  if (!docQ.data || !orgsQ.data || !employeesQ.data) {
    return <div className="card"><p>Завантаження…</p></div>;
  }

  const d = docQ.data;
  const org = orgsQ.data.items.find((o) => o.id === d.organization_id);
  const emp = employeesQ.data.find((e) => e.id === d.employee_id);

  if (!org || !emp) {
    return <div className="card"><p>Не знайдено організацію або підзвітну особу.</p></div>;
  }

  return (
    <div className="print-page kmd-print">
      <style>{`
        .kmd-print {
          background: white; color: black; max-width: 900px; margin: 0 auto;
          padding: 1.5rem;
          font-family: 'Times New Roman', serif; font-size: 10.5pt; line-height: 1.3;
        }
        .kmd-print h1 { font-size: 13pt; text-align: center; margin: 0; }
        .kmd-print .sub { text-align: center; font-size: 9pt; color: #555; margin: 4px 0; }
        .kmd-print .form-info { font-size: 9pt; color: #555; text-align: right; }
        .kmd-print .top-row { display: grid; grid-template-columns: 1fr auto; gap: 1rem; margin-bottom: 0.5rem; }
        .kmd-print .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-top: 0.75rem; }
        .kmd-print .field { margin: 0.4rem 0; }
        .kmd-print .label { font-size: 9pt; color: #555; }
        .kmd-print .underlined { border-bottom: 1px solid black; padding: 0 4px; min-height: 1em; display: inline-block; min-width: 60%; }
        .kmd-print table { width: 100%; border-collapse: collapse; font-size: 9.5pt; margin-top: 0.75rem; }
        .kmd-print th, .kmd-print td { border: 1px solid black; padding: 4px 6px; vertical-align: top; }
        .kmd-print th { background: #f5f5f5; font-weight: 600; text-align: center; }
        .kmd-print td.num { text-align: right; font-variant-numeric: tabular-nums; }
        .kmd-print td.center { text-align: center; }
        .kmd-print .totals-line { margin-top: 0.5rem; }
        .kmd-print .signs { margin-top: 1.5rem; display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 1.5rem; }
        .kmd-print .sign { border-bottom: 1px solid black; min-height: 22px; }
        .kmd-print .sign-label { font-size: 9pt; color: #555; margin-top: 4px; }
        .kmd-print .actions { display: flex; gap: 0.5rem; margin-bottom: 1rem; }
        .kmd-print .small { font-size: 9pt; color: #555; }
        @media print {
          .kmd-print .actions { display: none; }
          .kmd-print { padding: 0; max-width: none; }
          body { background: white !important; }
        }
      `}</style>

      <div className="actions print-hide">
        <button onClick={() => window.print()} className="primary">🖨 Друк / PDF</button>
        <button onClick={() => window.history.back()}>← Назад</button>
      </div>

      <div className="top-row">
        <div>
          <strong>{org.full_name || org.name}</strong>
          {org.edrpou && <div className="small">Код за ЄДРПОУ: {org.edrpou}</div>}
        </div>
        <div className="form-info">
          Типова форма № КМД-1<br />
          Затверджено наказом Мінфіну України<br />
          № 841 від 28.09.2015
        </div>
      </div>

      <h1>ЗВІТ</h1>
      <p className="sub">
        про використання коштів, виданих на відрядження або під звіт
      </p>
      <p className="sub" style={{ marginTop: '0.5rem' }}>
        № {d.document_number} від {formatDateUk(d.document_date)}
      </p>

      <div className="grid-2">
        <div>
          <div className="label">Підзвітна особа (П.І.Б.)</div>
          <div className="underlined" style={{ minWidth: '90%' }}>
            <strong>{emp.name}</strong>
          </div>
          {emp.code && <div className="small" style={{ marginTop: 4 }}>Таб. №: {emp.code}</div>}
        </div>
        <div>
          <div className="label">Посада</div>
          <div className="underlined" style={{ minWidth: '90%' }}>
            {emp.position_name ?? '—'}
          </div>
          {emp.inn && <div className="small" style={{ marginTop: 4 }}>РНОКПП: {emp.inn}</div>}
        </div>
      </div>

      <div className="field" style={{ marginTop: '0.5rem' }}>
        <span className="label">Призначення (мета відрядження / витрат): </span>
        <span className="underlined" style={{ minWidth: '70%' }}>{d.purpose ?? '—'}</span>
      </div>

      <table>
        <thead>
          <tr>
            <th style={{ width: 30 }}>№<br />з/п</th>
            <th style={{ width: 90 }}>Дата<br />документа</th>
            <th>Кому, за що і на підставі якого<br />документа сплачено</th>
            <th style={{ width: 110 }}>Сума<br />без ПДВ</th>
            <th style={{ width: 90 }}>ПДВ</th>
            <th style={{ width: 110 }}>Усього<br />з ПДВ</th>
            <th style={{ width: 60 }}>Кор.<br />рах.</th>
          </tr>
        </thead>
        <tbody>
          {d.items.map((it) => {
            const ctrName = it.contractor_id ? contractorName.get(it.contractor_id) : null;
            const detailParts = [
              ctrName,
              it.description,
              it.supplier_doc_number
                ? `документ № ${it.supplier_doc_number}${it.supplier_doc_date ? ` від ${formatShortDate(it.supplier_doc_date)}` : ''}`
                : null,
            ].filter((s): s is string => !!s);
            return (
              <tr key={it.id ?? it.line_number}>
                <td className="center">{it.line_number}</td>
                <td className="center">{formatShortDate(it.supplier_doc_date)}</td>
                <td>{detailParts.length ? detailParts.join('; ') : '—'}</td>
                <td className="num">{fmt2(it.amount_without_vat)}</td>
                <td className="num">{fmt2(it.vat_amount)}</td>
                <td className="num">{fmt2(it.total_amount)}</td>
                <td className="center">{it.expense_account_code ?? '92'}</td>
              </tr>
            );
          })}
          <tr style={{ fontWeight: 600 }}>
            <td colSpan={3} style={{ textAlign: 'right' }}>Усього:</td>
            <td className="num">{fmt2(d.amount_without_vat)}</td>
            <td className="num">{fmt2(d.vat_amount)}</td>
            <td className="num">{fmt2(d.total_amount)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <div className="totals-line" style={{ marginTop: '0.75rem' }}>
        <span className="label">Загальна сума витрат прописом: </span>
        <em>{amountToWordsUA(d.total_amount)}</em>
      </div>

      {Number(d.advance_received) > 0 && (() => {
        const received = Number(d.advance_received);
        const spent = Number(d.total_amount);
        const balance = received - spent;
        return (
          <table style={{ marginTop: '0.75rem', width: '60%' }}>
            <tbody>
              <tr>
                <td>Одержано в підзвіт (за РКО):</td>
                <td className="num"><strong>{fmt2(received)}</strong> грн</td>
              </tr>
              <tr>
                <td>Витрачено (за цим звітом):</td>
                <td className="num"><strong>{fmt2(spent)}</strong> грн</td>
              </tr>
              {balance > 0 && (
                <tr>
                  <td>Залишок до повернення в касу:</td>
                  <td className="num"><strong>{fmt2(balance)}</strong> грн</td>
                </tr>
              )}
              {balance < 0 && (
                <tr>
                  <td>Перевитрата (борг підприємства перед працівником):</td>
                  <td className="num"><strong>{fmt2(-balance)}</strong> грн</td>
                </tr>
              )}
            </tbody>
          </table>
        );
      })()}

      <div className="signs">
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">Керівник<br />(підпис, П.І.Б.)</div>
        </div>
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">Головний бухгалтер<br />(підпис, П.І.Б.)</div>
        </div>
        <div>
          <div className="sign">&nbsp;</div>
          <div className="sign-label">Підзвітна особа<br />(підпис, дата)</div>
        </div>
      </div>

      <p className="small" style={{ marginTop: '1rem' }}>
        Звіт затверджено в сумі <strong>{fmt2(d.total_amount)}</strong> грн
        ({amountToWordsUA(d.total_amount)}).
      </p>
    </div>
  );
}
